import { useEffect, useRef } from "react";
import { useApolloClient } from "@apollo/client/react";
import { InstrumentType, notEmpty, toBigInt } from "@orb-labs/types";
import { GetPositionsQuery, GetPerpOrderbookQuery, GetPredictionOrderbookQuery, GetRecentPerpTradesQuery, } from "../graphql/queries.js";
import { InstrumentLifecycleTransitionSubscription, InstrumentOrderbookUpdatesSubscription, InstrumentPriceUpdatesSubscription, NewInstrumentDiscoveredSubscription, PerpTradeUpdatesSubscription, PredictionTradeUpdatesSubscription, PredictionCommentUpdatesSubscription, SportMatchPredictionEventUpdatesSubscription, } from "../graphql/subscriptions.js";
import { PREDICTION_READ_FRAGMENT, PREDICTION_WRITE_FRAGMENT, SPORT_METADATA_FRAGMENT, } from "../graphql/liveUpdatesFragments.js";
import { prependToPredictionCategories, removeFromPredictionCategories } from "../utils/predictionCategoryCache.js";
import { useOrby } from "../components/OrbyProvider/OrbyProvider.js";
import { mergeLiveTimeSeriesChange } from "../utils/mergeLiveTimeSeriesChange.js";
import { UpdateBuffer } from "../data/UpdateBuffer.js";
import { prependToFungibleCategory, removeFromFungibleCategory, prependToFungibleCategoryLegacy, } from "../data/fungibleCategoryCache.js";
import { useLiveSubscriptionInterests, useLivePredictionTradesPublisher, useLivePredictionCommentsPublisher, } from "./LiveSubscriptionInterestsProvider.js";
import { usePlatformObservability } from "../observability/PlatformObservabilityContext.js";
import { ErrorEvents } from "../bullrun/analytics.js";
// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const PRICE_TO_CACHE_TYPENAME = {
    TokenPriceUpdate: "StandardizedFungibleToken",
    PerpPriceUpdate: "StandardizedPerpetual",
    PredictionEventPriceUpdate: "StandardizedPredictionEvent",
};
function mergePriceUpdates(existing, incoming) {
    if (existing.__typename === "PredictionEventPriceUpdate" && incoming.__typename === "PredictionEventPriceUpdate") {
        const merged = new Map((existing.outcomes ?? []).map((o) => [o.externalTokenId, o]));
        for (const o of incoming.outcomes ?? [])
            merged.set(o.externalTokenId, o);
        return {
            ...incoming,
            outcomes: Array.from(merged.values()),
            volume: incoming.volume ?? existing.volume,
        };
    }
    return incoming;
}
function orderbookBufferKey(p) {
    return notEmpty(p.marketId) && notEmpty(p.outcomeTokenId)
        ? `${p.standardizedInstrumentId}:${p.marketId}:${p.outcomeTokenId}`
        : p.standardizedInstrumentId;
}
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
/**
 * Subscribes to live instrument updates and writes them into the Apollo cache.
 * High-frequency feeds are buffered (500ms) to prevent excessive re-renders.
 *
 * Portfolio position values are recalculated once per price-flush cycle
 * (instead of per-token), further reducing cache churn.
 */
export function LiveUpdatesProvider({ children }) {
    const client = useApolloClient();
    const observability = usePlatformObservability();
    const { accountCluster } = useOrby();
    const userId = accountCluster?.user?.id;
    const accountClusterIdRef = useRef(accountCluster?.accountClusterId);
    accountClusterIdRef.current = accountCluster?.accountClusterId;
    const { priceInstrumentIds, orderbookInstrumentIds, sportMatchInstrumentIds, perpTradesInstrumentIds, predictionTradesInstrumentIds, predictionCommentsInstrumentIds, } = useLiveSubscriptionInterests();
    const publishPredictionTrades = useLivePredictionTradesPublisher();
    const publishPredictionComments = useLivePredictionCommentsPublisher();
    const priceIdsKey = priceInstrumentIds.join("|");
    const orderbookIdsKey = orderbookInstrumentIds.join("|");
    const sportMatchIdsKey = sportMatchInstrumentIds.join("|");
    const perpTradesIdsKey = perpTradesInstrumentIds.join("|");
    const predictionTradesIdsKey = predictionTradesInstrumentIds.join("|");
    const predictionCommentsIdsKey = predictionCommentsInstrumentIds.join("|");
    useEffect(() => {
        // ─── Flush: sport match updates ────────────────────────────────────
        const flushSportMatches = (updates) => {
            for (const payload of updates.values()) {
                try {
                    const ref = {
                        __typename: "StandardizedPredictionEvent",
                        id: payload.id,
                        standardizedInstrumentId: payload.standardizedInstrumentId,
                    };
                    const cacheId = client.cache.identify(ref);
                    if (!cacheId)
                        continue;
                    const existing = client.cache.readFragment({
                        id: cacheId,
                        fragment: SPORT_METADATA_FRAGMENT,
                        fragmentName: "SportMetadata",
                    });
                    if (!existing)
                        continue;
                    client.cache.writeFragment({
                        id: cacheId,
                        fragment: SPORT_METADATA_FRAGMENT,
                        fragmentName: "SportMetadata",
                        data: {
                            __typename: "StandardizedPredictionEvent",
                            id: payload.id,
                            standardizedInstrumentId: payload.standardizedInstrumentId,
                            sportMetadata: {
                                ...existing.sportMetadata,
                                isLive: payload.live,
                                gameStatus: payload.status ?? existing.sportMetadata?.gameStatus,
                                homeTeamScore: payload.homeTeamScore,
                                awayTeamScore: payload.awayTeamScore,
                                period: payload.period ?? existing.sportMetadata?.period,
                                elapsed: payload.elapsed ?? existing.sportMetadata?.elapsed,
                            },
                        },
                    });
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: sportMatch writeFragment failed", err);
                }
            }
        };
        // ─── Flush: price updates ──────────────────────────────────────────
        // eslint-disable-next-line complexity
        const flushPrices = (updates) => {
            const portfolioPrices = new Map();
            for (const payload of updates.values()) {
                try {
                    switch (payload.__typename) {
                        case "TokenPriceUpdate": {
                            const ref = {
                                __typename: "StandardizedFungibleToken",
                                id: payload.id,
                                standardizedInstrumentId: payload.standardizedInstrumentId,
                            };
                            const cacheId = client.cache.identify(ref);
                            if (!cacheId)
                                break;
                            const hasAny = payload.currentPrice ??
                                payload.currentMarketCap ??
                                payload.currentTotalVolume ??
                                payload.recentTrades ??
                                payload.recentActivity ??
                                payload.chartData;
                            if (!hasAny)
                                break;
                            client.cache.modify({
                                id: cacheId,
                                fields: {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    currentPrice: (existing) => {
                                        if (!payload.currentPrice)
                                            return existing;
                                        return mergeLiveTimeSeriesChange(existing, payload.currentPrice);
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    currentMarketCap: (existing) => {
                                        if (!payload.currentMarketCap)
                                            return existing;
                                        return mergeLiveTimeSeriesChange(existing, payload.currentMarketCap);
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    currentTotalVolume: (existing) => {
                                        if (!payload.currentTotalVolume)
                                            return existing;
                                        return mergeLiveTimeSeriesChange(existing, payload.currentTotalVolume);
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    recentTrades: (existing) => payload.recentTrades ?? existing,
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    marketData: (existingMarketData) => {
                                        if (!payload.recentActivity && !payload.recentTrades && !payload.chartData)
                                            return existingMarketData;
                                        if (!existingMarketData || typeof existingMarketData !== "object")
                                            return existingMarketData;
                                        const marketDataObj = existingMarketData;
                                        const existingChartData = marketDataObj.chartData;
                                        if (!existingChartData && !payload.recentTrades)
                                            return existingMarketData;
                                        const updatedChartData = existingChartData ? { ...existingChartData } : {};
                                        const updateTimeframe = (key, summaryData, ohlcData) => {
                                            if (!summaryData && !ohlcData)
                                                return;
                                            const existingTimeframe = updatedChartData[key];
                                            if (!existingTimeframe && !ohlcData)
                                                return;
                                            updatedChartData[key] = {
                                                ...(existingTimeframe || {}),
                                                ...(summaryData && { summary: summaryData }),
                                                ...(ohlcData && { priceOHLC: ohlcData }),
                                            };
                                        };
                                        updateTimeframe("fiveMinutes", payload.recentActivity?.fiveMinutes, payload.chartData?.fiveMinutes);
                                        updateTimeframe("oneHour", payload.recentActivity?.oneHour, payload.chartData?.oneHour);
                                        updateTimeframe("fourHours", payload.recentActivity?.fourHours, payload.chartData?.fourHours);
                                        updateTimeframe("oneDay", payload.recentActivity?.oneDay, payload.chartData?.oneDay);
                                        updateTimeframe("sevenDays", payload.recentActivity?.sevenDays, payload.chartData?.sevenDays);
                                        updateTimeframe("oneMonth", payload.recentActivity?.oneMonth, payload.chartData?.oneMonth);
                                        updateTimeframe("sixMonths", payload.recentActivity?.sixMonths, payload.chartData?.sixMonths);
                                        updateTimeframe("oneYear", payload.recentActivity?.oneYear, payload.chartData?.oneYear);
                                        updateTimeframe("all", payload.recentActivity?.all, payload.chartData?.all);
                                        return {
                                            ...marketDataObj,
                                            chartData: updatedChartData,
                                            ...(payload.recentTrades && { recentTrades: payload.recentTrades }),
                                        };
                                    },
                                },
                            });
                            if (payload.currentPrice?.end) {
                                portfolioPrices.set(payload.standardizedInstrumentId, {
                                    price: toBigInt(payload.currentPrice.end.value),
                                    timestamp: payload.currentPrice.end.timestamp,
                                });
                            }
                            break;
                        }
                        case "PerpPriceUpdate": {
                            const ref = {
                                __typename: "StandardizedPerpetual",
                                id: payload.id,
                                standardizedInstrumentId: payload.standardizedInstrumentId,
                            };
                            const cacheId = client.cache.identify(ref);
                            if (!cacheId)
                                break;
                            if (!payload.currentPrice && !payload.currentTotalVolume)
                                break;
                            client.cache.modify({
                                id: cacheId,
                                fields: {
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    currentPrice: (existing) => {
                                        if (!payload.currentPrice)
                                            return existing;
                                        return mergeLiveTimeSeriesChange(existing, payload.currentPrice);
                                    },
                                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                    currentTotalVolume: (existing) => {
                                        if (!payload.currentTotalVolume)
                                            return existing;
                                        return mergeLiveTimeSeriesChange(existing, payload.currentTotalVolume);
                                    },
                                },
                            });
                            break;
                        }
                        case "PredictionEventPriceUpdate": {
                            const ref = {
                                __typename: "StandardizedPredictionEvent",
                                id: payload.id,
                                standardizedInstrumentId: payload.standardizedInstrumentId,
                            };
                            const cacheId = client.cache.identify(ref);
                            if (!cacheId)
                                break;
                            const hasOutcomes = (payload.outcomes?.length ?? 0) > 0;
                            if (!payload.volume && !hasOutcomes)
                                break;
                            const existing = client.cache.readFragment({
                                id: cacheId,
                                fragment: PREDICTION_READ_FRAGMENT,
                                fragmentName: "PredictionRead",
                            });
                            if (!existing?.markets)
                                break;
                            const outcomeByToken = new Map((payload.outcomes ?? []).map((o) => [o.externalTokenId, o.price]));
                            const updatedMarkets = existing.markets.map((m) => ({
                                ...m,
                                outcomes: m.outcomes?.map((o) => {
                                    if (!outcomeByToken.has(o.externalTokenId))
                                        return o;
                                    return {
                                        ...o,
                                        price: mergeLiveTimeSeriesChange(o.price, outcomeByToken.get(o.externalTokenId)),
                                    };
                                }),
                            }));
                            client.cache.writeFragment({
                                id: cacheId,
                                fragment: PREDICTION_WRITE_FRAGMENT,
                                fragmentName: "PredictionWrite",
                                data: {
                                    __typename: "StandardizedPredictionEvent",
                                    id: payload.id,
                                    standardizedInstrumentId: payload.standardizedInstrumentId,
                                    ...(notEmpty(payload.volume) && {
                                        volume: mergeLiveTimeSeriesChange(existing.volume, payload.volume),
                                    }),
                                    markets: updatedMarkets,
                                },
                            });
                            if (hasOutcomes) {
                                client.cache.modify({
                                    id: cacheId,
                                    fields: {
                                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                        sportMetadata: (existingSportMeta) => {
                                            if (!existingSportMeta)
                                                return existingSportMeta;
                                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                                            const updateOutcome = (outcome) => {
                                                if (!outcome?.externalTokenId)
                                                    return outcome;
                                                const updatedPrice = outcomeByToken.get(outcome.externalTokenId);
                                                return updatedPrice ? { ...outcome, price: updatedPrice } : outcome;
                                            };
                                            return {
                                                ...existingSportMeta,
                                                homeTeamWin: updateOutcome(existingSportMeta.homeTeamWin),
                                                awayTeamWin: updateOutcome(existingSportMeta.awayTeamWin),
                                                draw: updateOutcome(existingSportMeta.draw),
                                            };
                                        },
                                    },
                                });
                            }
                            break;
                        }
                        default:
                            break;
                    }
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: price flush failed", err);
                }
            }
            // Batched portfolio recalculation — one cache read/write per flush instead of per-token
            if (portfolioPrices.size > 0 && accountClusterIdRef.current) {
                const accountClusterId = accountClusterIdRef.current;
                try {
                    const positions = client.cache.readQuery({
                        query: GetPositionsQuery,
                        variables: { input: { accountClusterId } },
                    })?.getPositions;
                    if (!positions)
                        throw null;
                    const recalcCategory = (categoryPositions) => {
                        if (!categoryPositions?.length)
                            return { positions: categoryPositions, subtotal: BigInt(0) };
                        let subtotal = BigInt(0);
                        let changed = false;
                        const updatedPositions = categoryPositions.map((pos) => {
                            const priceUpdate = portfolioPrices.get(pos.standardizedInstrumentId);
                            if (priceUpdate && pos.total?.amount) {
                                const amount = toBigInt(pos.total.amount);
                                const decimals = pos.total.currency?.decimals ?? 18;
                                const newFiatValue = decimals > 0 ? (amount * priceUpdate.price) / BigInt(10 ** decimals) : BigInt(0);
                                subtotal += newFiatValue;
                                changed = true;
                                return {
                                    ...pos,
                                    totalValueInFiat: {
                                        ...pos.totalValueInFiat,
                                        end: { timestamp: priceUpdate.timestamp, value: newFiatValue.toString() },
                                    },
                                };
                            }
                            subtotal += toBigInt(pos.totalValueInFiat?.end?.value);
                            return pos;
                        });
                        return { positions: changed ? updatedPositions : categoryPositions, subtotal };
                    };
                    const fungible = recalcCategory(positions.fungibleTokenPositions?.positions);
                    const stablecoin = recalcCategory(positions.stablecoinPositions?.positions);
                    if (fungible.positions === positions.fungibleTokenPositions?.positions &&
                        stablecoin.positions === positions.stablecoinPositions?.positions) {
                        throw null;
                    }
                    const perpSubtotal = toBigInt(positions.perpetualPositions?.totalValueInFiat?.end?.value);
                    const predictionSubtotal = toBigInt(positions.predictionPositions?.totalValueInFiat?.end?.value);
                    const latestTimestamp = Array.from(portfolioPrices.values()).pop()?.timestamp;
                    const newTotal = (fungible.subtotal + stablecoin.subtotal + perpSubtotal + predictionSubtotal).toString();
                    client.cache.writeQuery({
                        query: GetPositionsQuery,
                        variables: { input: { accountClusterId } },
                        data: {
                            getPositions: {
                                ...positions,
                                fungibleTokenPositions: {
                                    ...positions.fungibleTokenPositions,
                                    positions: fungible.positions,
                                    totalValueInFiat: {
                                        ...positions.fungibleTokenPositions?.totalValueInFiat,
                                        end: { timestamp: latestTimestamp, value: fungible.subtotal.toString() },
                                    },
                                },
                                stablecoinPositions: {
                                    ...positions.stablecoinPositions,
                                    positions: stablecoin.positions,
                                    totalValueInFiat: {
                                        ...positions.stablecoinPositions?.totalValueInFiat,
                                        end: { timestamp: latestTimestamp, value: stablecoin.subtotal.toString() },
                                    },
                                },
                                totalValueInFiat: {
                                    ...positions.totalValueInFiat,
                                    end: { timestamp: latestTimestamp, value: newTotal },
                                },
                            },
                        },
                    });
                }
                catch {
                    /* positions not cached yet or tokens not in portfolio, skip */
                }
            }
        };
        // ─── Flush: orderbook updates ──────────────────────────────────────
        const flushOrderbooks = (updates) => {
            for (const payload of updates.values()) {
                try {
                    const { standardizedInstrumentId, orderBook, marketId, outcomeTokenId } = payload;
                    if (!notEmpty(marketId) && !notEmpty(outcomeTokenId)) {
                        const variables = { input: { standardizedInstrumentId } };
                        const existing = client.cache.readQuery({
                            query: GetPerpOrderbookQuery,
                            variables,
                        });
                        client.cache.writeQuery({
                            query: GetPerpOrderbookQuery,
                            variables,
                            data: {
                                getPerpOrderbook: {
                                    ...existing?.getPerpOrderbook,
                                    orderBook,
                                    tickOptions: existing?.getPerpOrderbook?.tickOptions ?? [],
                                    spread: existing?.getPerpOrderbook?.spread ?? "0",
                                    spreadPercent: existing?.getPerpOrderbook?.spreadPercent ?? "0",
                                    midPrice: existing?.getPerpOrderbook?.midPrice ?? "0",
                                },
                            },
                        });
                    }
                    else if (notEmpty(marketId) && notEmpty(outcomeTokenId)) {
                        const variables = {
                            input: { standardizedInstrumentId, marketId, outcomeTokenId },
                        };
                        const existing = client.cache.readQuery({
                            query: GetPredictionOrderbookQuery,
                            variables,
                        });
                        client.cache.writeQuery({
                            query: GetPredictionOrderbookQuery,
                            variables,
                            data: {
                                getPredictionOrderbook: {
                                    ...existing?.getPredictionOrderbook,
                                    orderBook,
                                    tickOptions: existing?.getPredictionOrderbook?.tickOptions ?? [],
                                    spread: existing?.getPredictionOrderbook?.spread ?? "0",
                                    spreadPercent: existing?.getPredictionOrderbook?.spreadPercent ?? "0",
                                    midPrice: existing?.getPredictionOrderbook?.midPrice ?? "0",
                                },
                            },
                        });
                    }
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: orderbook flush failed", err);
                }
            }
        };
        // ─── Flush: perp trade updates ──────────────────────────────────
        const MAX_CACHED_PERP_TRADES = 50;
        const flushPerpTrades = (updates) => {
            for (const [instrumentId, incoming] of updates.entries()) {
                try {
                    const variables = { input: { standardizedInstrumentId: instrumentId, limit: MAX_CACHED_PERP_TRADES } };
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    let existing;
                    try {
                        existing = client.cache.readQuery({
                            query: GetRecentPerpTradesQuery,
                            variables,
                        });
                    }
                    catch {
                        /* Cache miss — first subscription event before the REST query has returned. */
                    }
                    const prevTrades = (existing?.getRecentPerpTrades?.trades ?? []);
                    const incomingTrades = incoming.map((p) => p.trade);
                    const seen = new Set(incomingTrades.map((t) => t.tradeId).filter(Boolean));
                    const deduped = prevTrades.filter((t) => !t.tradeId || !seen.has(t.tradeId));
                    const merged = [...incomingTrades, ...deduped].slice(0, MAX_CACHED_PERP_TRADES);
                    client.cache.writeQuery({
                        query: GetRecentPerpTradesQuery,
                        variables,
                        data: {
                            getRecentPerpTrades: {
                                __typename: "RecentPerpTradesConnection",
                                trades: merged,
                            },
                        },
                    });
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: perpTrades flush failed", err);
                }
            }
        };
        // ─── Flush: prediction trade updates ────────────────────────────
        const flushPredictionTrades = (updates) => {
            for (const [instrumentId, incoming] of updates.entries()) {
                const trades = incoming.map((p) => ({
                    standardizedInstrumentId: instrumentId,
                    outcomeTokenId: p.trade.outcomeTokenId,
                    conditionId: p.trade.conditionId,
                    outcomeName: p.trade.outcomeName ?? null,
                    side: p.trade.side,
                    size: Number(p.trade.size),
                    price: Number(p.trade.price),
                    volumeUsd: Number(p.trade.volumeUsd),
                    timestamp: new Date(p.trade.timestamp),
                    txHash: p.trade.txHash ?? null,
                }));
                try {
                    publishPredictionTrades(instrumentId, trades);
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: predictionTrades publish failed", err);
                }
            }
        };
        // ─── Flush: prediction comment updates ─────────────────────────
        const flushPredictionComments = (updates) => {
            for (const [instrumentId, incoming] of updates.entries()) {
                const comments = incoming.map((p) => ({
                    id: p.comment.id,
                    body: p.comment.body,
                    username: p.comment.username,
                    profileImage: p.comment.profileImage ?? null,
                    userAddress: p.comment.userAddress,
                    createdAt: new Date(p.comment.createdAt),
                    reactionCount: p.comment.reactionCount,
                    parentCommentId: p.comment.parentCommentId ?? null,
                }));
                try {
                    publishPredictionComments(instrumentId, comments);
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: predictionComments publish failed", err);
                }
            }
        };
        // ─── Buffers ───────────────────────────────────────────────────────
        const priceBuffer = new UpdateBuffer((p) => p.standardizedInstrumentId, flushPrices, {
            merge: mergePriceUpdates,
        });
        const orderbookBuffer = new UpdateBuffer(orderbookBufferKey, flushOrderbooks);
        const sportMatchBuffer = new UpdateBuffer((p) => p.standardizedInstrumentId, flushSportMatches);
        const perpTradesBuffer = new UpdateBuffer((batch) => batch[0]?.standardizedInstrumentId ?? "", flushPerpTrades, {
            merge: (existing, incoming) => [...existing, ...incoming],
        });
        const predictionTradesBuffer = new UpdateBuffer((batch) => batch[0]?.standardizedInstrumentId ?? "", flushPredictionTrades, {
            merge: (existing, incoming) => [...existing, ...incoming],
        });
        const predictionCommentsBuffer = new UpdateBuffer((batch) => batch[0]?.standardizedInstrumentId ?? "", flushPredictionComments, {
            merge: (existing, incoming) => [...existing, ...incoming],
        });
        // ─── Subscriptions ────────────────────────────────────────────────
        const noopSub = { unsubscribe: () => { } };
        const sportMatchSub = sportMatchInstrumentIds.length > 0
            ? client
                .subscribe({
                query: SportMatchPredictionEventUpdatesSubscription,
                variables: { input: { standardizedInstrumentIds: sportMatchInstrumentIds } },
            })
                .subscribe({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                next: (result) => {
                    const payload = result.data?.sportMatchPredictionEventUpdates;
                    if (!payload?.standardizedInstrumentId || !notEmpty(payload.id))
                        return;
                    sportMatchBuffer.push(payload);
                },
                error: (err) => {
                    observability.error("LiveUpdatesProvider: sportMatchPredictionEventUpdates error", err);
                    observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                        subscription: "sportMatchPredictionEventUpdates",
                        error_message: err instanceof Error ? err.message : String(err),
                    });
                },
            })
            : noopSub;
        const priceSub = priceInstrumentIds.length > 0
            ? client
                .subscribe({
                query: InstrumentPriceUpdatesSubscription,
                variables: { input: { standardizedInstrumentIds: priceInstrumentIds } },
            })
                .subscribe({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                next: (result) => {
                    const payload = result.data
                        ?.instrumentPriceUpdates;
                    if (!payload?.id || !payload.standardizedInstrumentId)
                        return;
                    const cacheTypename = PRICE_TO_CACHE_TYPENAME[payload.__typename];
                    if (!cacheTypename ||
                        !client.cache.identify({
                            __typename: cacheTypename,
                            id: payload.id,
                            standardizedInstrumentId: payload.standardizedInstrumentId,
                        })) {
                        return;
                    }
                    priceBuffer.push(payload);
                },
                error: (err) => {
                    observability.error("LiveUpdatesProvider: instrumentPriceUpdates error", err);
                    observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                        subscription: "instrumentPriceUpdates",
                        error_message: err instanceof Error ? err.message : String(err),
                    });
                },
            })
            : noopSub;
        const orderbookSub = orderbookInstrumentIds.length > 0
            ? client
                .subscribe({
                query: InstrumentOrderbookUpdatesSubscription,
                variables: { input: { standardizedInstrumentIds: orderbookInstrumentIds } },
            })
                .subscribe({
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                next: (result) => {
                    const payload = result.data?.instrumentOrderbookUpdates;
                    if (!payload?.standardizedInstrumentId || !payload.orderBook)
                        return;
                    orderbookBuffer.push(payload);
                },
                error: (err) => {
                    observability.error("LiveUpdatesProvider: instrumentOrderbookUpdates error", err);
                    observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                        subscription: "instrumentOrderbookUpdates",
                        error_message: err instanceof Error ? err.message : String(err),
                    });
                },
            })
            : noopSub;
        // New instrument discovered — fungible
        const newInstrumentSub = client
            .subscribe({
            query: NewInstrumentDiscoveredSubscription,
            variables: { input: { instrumentType: InstrumentType.FUNGIBLE_TOKEN } },
        })
            .subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const data = result.data;
                const instrument = data?.newInstrumentDiscovered;
                if (!instrument?.standardizedInstrumentId)
                    return;
                try {
                    prependToFungibleCategory(client, "new", instrument, userId);
                }
                catch (err) {
                    // Cache entry "not present" is the benign, expected case when the
                    // consumer hasn't issued the matching query yet — only one of the
                    // web/legacy patterns is active at a time. Log at warn so a genuine
                    // cache-write bug is visible in observability without paging.
                    observability.warn("LiveUpdatesProvider: prependToFungibleCategory (web pattern) skipped", {
                        standardizedInstrumentId: instrument.standardizedInstrumentId,
                        error_message: err instanceof Error ? err.message : String(err),
                    });
                }
                try {
                    prependToFungibleCategoryLegacy(client, instrument);
                }
                catch (err) {
                    observability.warn("LiveUpdatesProvider: prependToFungibleCategoryLegacy skipped", {
                        standardizedInstrumentId: instrument.standardizedInstrumentId,
                        error_message: err instanceof Error ? err.message : String(err),
                    });
                }
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: newInstrumentDiscovered error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "newInstrumentDiscovered",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        });
        // New instrument discovered — prediction
        const newPredictionInstrumentSub = client
            .subscribe({
            query: NewInstrumentDiscoveredSubscription,
            variables: { input: { instrumentType: InstrumentType.PREDICTION_MARKET } },
        })
            .subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const data = result.data;
                const instrument = data?.newInstrumentDiscovered;
                if (!instrument?.standardizedInstrumentId)
                    return;
                try {
                    prependToPredictionCategories(client, instrument);
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: newInstrumentDiscovered prediction cache write failed", err);
                }
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: newInstrumentDiscovered prediction error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "newInstrumentDiscoveredPrediction",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        });
        // Lifecycle transitions
        const lifecycleTransitionSub = client.subscribe({ query: InstrumentLifecycleTransitionSubscription }).subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const transition = result.data?.instrumentLifecycleTransition;
                if (!transition?.standardizedInstrumentId)
                    return;
                try {
                    if (transition.toStage === "resolved") {
                        removeFromPredictionCategories(client, transition.standardizedInstrumentId);
                        return;
                    }
                    // Fungible lifecycle transitions (new → migrating → migrated) — both cache patterns
                    if (transition.fromStage) {
                        try {
                            removeFromFungibleCategory(client, transition.fromStage, transition.standardizedInstrumentId, userId);
                        }
                        catch (err) {
                            observability.warn("LiveUpdatesProvider: removeFromFungibleCategory (web pattern) skipped", {
                                fromStage: transition.fromStage,
                                standardizedInstrumentId: transition.standardizedInstrumentId,
                                error_message: err instanceof Error ? err.message : String(err),
                            });
                        }
                    }
                    if (transition.toStage && transition.instrument) {
                        try {
                            prependToFungibleCategory(client, transition.toStage, transition.instrument, userId);
                        }
                        catch (err) {
                            observability.warn("LiveUpdatesProvider: prependToFungibleCategory (web pattern) skipped", {
                                toStage: transition.toStage,
                                standardizedInstrumentId: transition.standardizedInstrumentId,
                                error_message: err instanceof Error ? err.message : String(err),
                            });
                        }
                    }
                }
                catch (err) {
                    observability.error("LiveUpdatesProvider: lifecycleTransition cache update failed", err);
                }
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: lifecycleTransition error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "instrumentLifecycleTransition",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        });
        // Per-instrument perp trade subscriptions
        const perpTradeSubs = perpTradesInstrumentIds.map((instrumentId) => client
            .subscribe({
            query: PerpTradeUpdatesSubscription,
            variables: { input: { standardizedInstrumentId: instrumentId } },
        })
            .subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const trade = result.data?.perpTradeUpdates;
                if (!trade)
                    return;
                const payload = { standardizedInstrumentId: instrumentId, trade };
                perpTradesBuffer.push([payload]);
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: perpTradeUpdates error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "perpTradeUpdates",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        }));
        // Per-instrument prediction trade subscriptions
        const predictionTradeSubs = predictionTradesInstrumentIds.map((instrumentId) => client
            .subscribe({
            query: PredictionTradeUpdatesSubscription,
            variables: { input: { standardizedInstrumentId: instrumentId } },
        })
            .subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const trade = result.data
                    ?.predictionTradeUpdates;
                if (!trade)
                    return;
                const payload = { standardizedInstrumentId: instrumentId, trade };
                predictionTradesBuffer.push([payload]);
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: predictionTradeUpdates error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "predictionTradeUpdates",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        }));
        // Per-instrument prediction comment subscriptions
        const predictionCommentSubs = predictionCommentsInstrumentIds.map((instrumentId) => client
            .subscribe({
            query: PredictionCommentUpdatesSubscription,
            variables: { input: { standardizedInstrumentId: instrumentId } },
        })
            .subscribe({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            next: (result) => {
                const comment = result.data?.predictionCommentUpdates;
                if (!comment)
                    return;
                const payload = { standardizedInstrumentId: instrumentId, comment };
                predictionCommentsBuffer.push([payload]);
            },
            error: (err) => {
                observability.error("LiveUpdatesProvider: predictionCommentUpdates error", err);
                observability.capture(ErrorEvents.WEBSOCKET_ERROR, {
                    subscription: "predictionCommentUpdates",
                    error_message: err instanceof Error ? err.message : String(err),
                });
            },
        }));
        // ─── Cleanup ──────────────────────────────────────────────────────
        return () => {
            sportMatchSub.unsubscribe();
            priceSub.unsubscribe();
            orderbookSub.unsubscribe();
            newInstrumentSub.unsubscribe();
            newPredictionInstrumentSub.unsubscribe();
            lifecycleTransitionSub.unsubscribe();
            for (const sub of perpTradeSubs)
                sub.unsubscribe();
            for (const sub of predictionTradeSubs)
                sub.unsubscribe();
            for (const sub of predictionCommentSubs)
                sub.unsubscribe();
            sportMatchBuffer.dispose();
            priceBuffer.dispose();
            orderbookBuffer.dispose();
            perpTradesBuffer.dispose();
            predictionTradesBuffer.dispose();
            predictionCommentsBuffer.dispose();
        };
        // Depend on the joined "*IdsKey" strings rather than the raw arrays — the
        // arrays are not memoized by content, so resubscribing on stable-key
        // equality is the whole point of this layer. The raw arrays are read
        // inside the effect but intentionally omitted from deps.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        client,
        priceIdsKey,
        orderbookIdsKey,
        sportMatchIdsKey,
        perpTradesIdsKey,
        predictionTradesIdsKey,
        predictionCommentsIdsKey,
        userId,
        publishPredictionTrades,
        publishPredictionComments,
        observability,
    ]);
    return children;
}
